<template>
  <div class="viewer-image-overlays">
    <magnification-selector
      class="magnification-selector-overlay"
      :image="image"
      :zoom="zoom"
      @fit="$emit('fit-zoom')"
      @setMagnification="$emit('set-magnification', $event)"
    />

    <div class="image-draw-tools">
      <draw-tools :index="index" @screenshot="$emit('take-screenshot')" />
    </div>

    <annotation-details-container
      class="annotation-details-overlay"
      :index="index"
      @addTerm="addTerm"
      @addTrack="addTrack"
      @centerView="centerView({ annot: $event, sameView: true })"
      @delete="handleDeletion"
      @select="selectAnnotation"
      @updateTermsOrTracks="updateTermsOrTracks"
      @updateProperties="updateProperties"
    />

    <div ref="overview" class="custom-overview" :style="overviewStyle">
      <scale-line
        class="overview-scale-line"
        :image="image"
        :zoom="zoom"
        :mousePosition="projectedMousePosition"
      />
      <div class="overview-image" :style="overviewImageStyle" @click="handleOverviewClick">
        <img :src="overviewImageUrl" alt="" draggable="false" />
        <div
          class="overview-trail-layers"
          :class="{ 'is-hidden': !isOverviewTrailVisible }"
          aria-hidden="true"
        >
          <canvas
            v-for="bucket in overviewTrailBuckets"
            :key="bucket.magnification"
            :ref="getOverviewTrailLayerRef(bucket)"
            :data-magnification="bucket.magnification"
            :style="getOverviewTrailLayerStyle(bucket)"
            class="overview-trail-canvas"
          ></canvas>
        </div>
        <div class="overview-viewport" :style="overviewViewportStyle"></div>
      </div>
      <div class="overview-trail-controls">
        <button
          type="button"
          class="overview-trail-toggle"
          :class="{ 'is-off': !showOverviewTrail }"
          :aria-pressed="String(showOverviewTrail)"
          :aria-label="showOverviewTrail ? 'Hide viewed area trail' : 'Show viewed area trail'"
          :title="showOverviewTrail ? 'Hide viewed area trail' : 'Show viewed area trail'"
          @click.stop="showOverviewTrail = !showOverviewTrail"
        >
          <i :class="showOverviewTrail ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
        </button>
        <button
          type="button"
          class="overview-trail-reset"
          aria-label="Reset viewed area trail"
          title="Reset viewed area trail"
          @click.stop="resetOverviewTrail"
        >
          <i class="fas fa-undo-alt"></i>
        </button>
      </div>
    </div>

    <add-ontology-modal
      :active="showAddOntologyModal"
      :index="index"
      @close="$emit('close-add-ontology-modal')"
    />
  </div>
</template>

<script>
import ScaleLine from './ScaleLine';
import MagnificationSelector from './MagnificationSelector';
import DrawTools from './DrawTools';
import AnnotationDetailsContainer from './AnnotationDetailsContainer';
import { Annotation } from '@/api';
import { Action, listAnnotationsInGroup, updateAnnotationLinkProperties, updateTermProperties, updateTrackProperties } from '@/utils/annotation-utils';
import { getAvailableMagnifications, getMagnificationForZoom } from './magnifications';

const OVERVIEW_TRAIL_MIN_MAGNIFICATION = 2;
const OVERVIEW_TRAIL_HEATMAP_COLORS = ['#1a9850', '#91cf60', '#fee08b', '#fc8d59', '#d73027'];
const OVERVIEW_TRAIL_MIN_OPACITY = 0.3;
const OVERVIEW_TRAIL_MAX_OPACITY = 0.6;

export default {
  name: 'viewer-image-overlays',
  components: {
    ScaleLine,
    MagnificationSelector,
    DrawTools,
    AnnotationDetailsContainer,
    AddOntologyModal: () => import('./panels/AddOntologyModal.vue')
  },
  props: {
    image: {
      type: Object,
      required: true
    },
    index: {
      type: String,
      required: true
    },
    overviewStyle: {
      type: Object,
      default: () => ({})
    },
    overviewImageUrl: {
      type: String,
      default: null
    },
    overviewViewport: {
      type: Object,
      default: null
    },
    projectedMousePosition: {
      type: Array,
      required: true
    },
    showAddOntologyModal: Boolean,
    zoom: Number
  },
  data() {
    return {
      hasOverviewTrailCanvasContent: false,
      latestOverviewTrailKey: null,
      overviewTrailTouchedBuckets: new Set(),
      showOverviewTrail: true,
    };
  },
  computed: {
    viewerModule() {
      return this.$store.getters['currentProject/viewerModulePath'];
    },
    imageModule() {
      return this.$store.getters['currentProject/imageModulePath'](this.index);
    },
    viewerWrapper() {
      return this.$store.getters['currentProject/currentViewer'];
    },
    imageGroupId() {
      return this.$store.getters[this.imageModule + 'imageGroupId'];
    },
    overviewViewportStyle() {
      return this.overviewViewport || {};
    },
    overviewImageStyle() {
      return {
        width: this.overviewStyle.width,
        height: this.overviewStyle.height,
      };
    },
    currentMagnification() {
      return getMagnificationForZoom(this.image, this.zoom) || 0;
    },
    overviewTrailBuckets() {
      let magnifications = getAvailableMagnifications(this.image)
        .filter(magnification => magnification > OVERVIEW_TRAIL_MIN_MAGNIFICATION);
      let maxIndex = Math.max(magnifications.length - 1, 1);
      return magnifications.map((magnification, index) => ({
        magnification,
        color: this.getOverviewTrailColor(index, maxIndex),
        opacity: this.getOverviewTrailOpacity(index, maxIndex),
      }));
    },
    isOverviewTrailVisible() {
      return this.showOverviewTrail && this.currentMagnification > OVERVIEW_TRAIL_MIN_MAGNIFICATION;
    },
    copiedAnnot: {
      get() {
        return this.viewerWrapper.copiedAnnot;
      },
      set(annot) {
        this.$store.commit(this.viewerModule + 'setCopiedAnnot', annot);
      }
    },
  },
  watch: {
    overviewViewport: {
      handler: 'recordOverviewTrail',
      immediate: true,
    },
    zoom: 'handleOverviewTrailZoomChange',
    overviewStyle: {
      handler: 'handleOverviewTrailLayoutChange',
      deep: true,
    },
    'image.id': 'handleOverviewImageChange',
  },
  mounted() {
    this.$nextTick(() => {
      this.resizeOverviewTrailCanvas();
      if (!this.restoreOverviewTrailCanvas(this.image?.id)) {
        this.recordOverviewTrail();
      }
    });
  },
  beforeDestroy() {
    this.persistOverviewTrailCanvas(this.image?.id);
  },
  methods: {
    addTerm(term) {
      this.$store.dispatch(this.viewerModule + 'addTerm', term);
    },
    addTrack(track) {
      this.$store.dispatch(this.viewerModule + 'refreshTracks', { idImage: track.image });
    },
    async updateTermsOrTracks(annot) {
      let updatedAnnot = await annot.clone().fetch();
      updatedAnnot.imageGroup = this.imageGroupId;
      await updateTermProperties(updatedAnnot);
      await updateTrackProperties(updatedAnnot);
      await updateAnnotationLinkProperties(updatedAnnot);

      if (updatedAnnot.aiVisualizationType === 'confidence_heatmap') {
        await this.$store.dispatch(this.imageModule + 'updateMalariaConfidenceAnnotation', updatedAnnot);
      }

      this.$eventBus.$emit('editAnnotation', updatedAnnot);
      this.$store.commit(this.imageModule + 'changeAnnotSelectedFeature', { indexFeature: 0, annot: updatedAnnot });
    },
    updateProperties() {
      this.$store.dispatch(this.imageModule + 'refreshProperties', this.index);
    },
    async handleDeletion(annot) {
      this.$store.commit(this.imageModule + 'addAction', { annot, type: Action.DELETE });

      if (annot.group) {
        let editedAnnots = [];
        if (annot.annotationLink.length === 2) {
          let otherId = annot.annotationLink.filter(al => al.annotation !== annot.id)[0].annotation;
          let other = await Annotation.fetch(otherId);
          other.imageGroup = annot.imageGroup;
          await updateTermProperties(other);
          await updateTrackProperties(other);
          await updateAnnotationLinkProperties(other);

          editedAnnots = [other];
        } else {
          editedAnnots = await listAnnotationsInGroup(annot.project, annot.group);
        }
        editedAnnots.forEach(a => {
          this.$eventBus.$emit('editAnnotation', a);
          if (this.copiedAnnot && a.id === this.copiedAnnot.id) {
            let copiedAnnot = this.copiedAnnot.clone();
            copiedAnnot.annotationLink = a.annotationLink;
            copiedAnnot.group = a.group;
            this.copiedAnnot = copiedAnnot;
          }
        });
      }

      this.$eventBus.$emit('deleteAnnotation', annot);
    },
    selectAnnotation({ annot, options = {} }) {
      let index = options.trySameView ? this.index : null;
      this.$eventBus.$emit('selectAnnotation', { index, annot, center: true });
    },
    centerView({ annot, sameView = false }) {
      if (sameView) {
        this.$emit('center-view', annot);
      } else {
        this.$eventBus.$emit('selectAnnotation', { index: null, annot, center: true });
      }
    },
    handleOverviewClick(event) {
      this.$emit('overview-center', this.getOverviewCoordinate(event));
    },
    getOverviewCoordinate(event) {
      let bounds = event.currentTarget.getBoundingClientRect();
      let relativeX = (event.clientX - bounds.left) / bounds.width;
      let relativeY = (event.clientY - bounds.top) / bounds.height;
      return [
        relativeX * this.image.width,
        this.image.height - (relativeY * this.image.height),
      ];
    },
    handleOverviewImageChange(imageId, previousImageId) {
      this.persistOverviewTrailCanvas(previousImageId);
      this.hasOverviewTrailCanvasContent = false;
      this.latestOverviewTrailKey = null;
      this.overviewTrailTouchedBuckets = new Set();
      this.$nextTick(() => {
        this.resizeOverviewTrailCanvas();
        if (!this.restoreOverviewTrailCanvas(imageId)) {
          this.recordOverviewTrail();
        }
      });
    },
    handleOverviewTrailZoomChange() {
      this.$nextTick(() => {
        this.resizeOverviewTrailCanvas();
        this.composeOverviewTrailCanvas();
        this.recordOverviewTrail();
      });
    },
    handleOverviewTrailLayoutChange() {
      this.$nextTick(() => {
        this.resizeOverviewTrailCanvas();
        this.composeOverviewTrailCanvas();
      });
    },
    getOverviewTrailLayerSnapshots(imageId) {
      let getter = this.$store.getters[this.imageModule + 'overviewTrailLayerSnapshots'];
      if (!getter || imageId === undefined || imageId === null) {
        return {};
      }
      return getter(imageId);
    },
    persistOverviewTrailCanvas(imageId) {
      if (!imageId || !this.hasOverviewTrailCanvasContent) {
        return;
      }

      this.overviewTrailBuckets
        .filter(bucket => this.overviewTrailTouchedBuckets.has(bucket.magnification))
        .forEach(bucket => {
          let layerCanvas = this.getOverviewTrailLayerCanvas(bucket);
          if (!layerCanvas || typeof layerCanvas.toDataURL !== 'function') {
            return;
          }
          try {
            let snapshot = layerCanvas.toDataURL('image/png');
            this.$store.commit(this.imageModule + 'setOverviewTrailLayerSnapshot', {
              imageId,
              magnification: bucket.magnification,
              snapshot
            });
          } catch (error) {
            // Canvas snapshots are best-effort UI state; losing one should not break viewer navigation.
          }
        });
    },
    restoreOverviewTrailCanvas(imageId) {
      this.clearOverviewTrailCanvases();
      let snapshots = this.getOverviewTrailLayerSnapshots(imageId);
      let snapshotEntries = Object.entries(snapshots);
      if (!snapshotEntries.length) {
        return false;
      }

      snapshotEntries.forEach(([magnification, snapshot]) => {
        let bucket = this.overviewTrailBuckets.find(item => String(item.magnification) === String(magnification));
        if (!bucket || !snapshot) {
          return;
        }
        let trailImage = new Image();
        trailImage.onload = () => {
          if (String(this.image?.id) !== String(imageId)) {
            return;
          }
          let layerCanvas = this.getOverviewTrailLayerCanvas(bucket);
          let context = layerCanvas?.getContext('2d');
          if (!layerCanvas || !context) {
            return;
          }
          context.clearRect(0, 0, layerCanvas.width, layerCanvas.height);
          context.drawImage(trailImage, 0, 0, layerCanvas.width, layerCanvas.height);
          this.overviewTrailTouchedBuckets.add(bucket.magnification);
          this.hasOverviewTrailCanvasContent = true;
          this.composeOverviewTrailCanvas();
          this.recordOverviewTrail();
        };
        trailImage.onerror = () => this.recordOverviewTrail();
        trailImage.src = snapshot;
      });
      return true;
    },
    resetOverviewTrail() {
      this.clearOverviewTrailCanvases();
      this.hasOverviewTrailCanvasContent = false;
      this.latestOverviewTrailKey = null;
      this.overviewTrailTouchedBuckets = new Set();
      this.$store.commit(this.imageModule + 'clearOverviewTrailLayerSnapshots', this.image?.id);
    },
    clearOverviewTrailCanvases() {
      this.overviewTrailBuckets.forEach(bucket => {
        let layerCanvas = this.getOverviewTrailLayerCanvas(bucket);
        let layerContext = layerCanvas?.getContext('2d');
        if (layerCanvas && layerContext) {
          layerContext.clearRect(0, 0, layerCanvas.width, layerCanvas.height);
        }
      });
    },
    recordOverviewTrail() {
      if (!this.overviewViewport || this.currentMagnification <= OVERVIEW_TRAIL_MIN_MAGNIFICATION) {
        return;
      }

      let viewport = this.normalizeOverviewViewport(this.overviewViewport);
      let bucket = this.getOverviewTrailBucket(this.currentMagnification);
      if (!viewport) {
        return;
      }
      if (this.isDuplicateOverviewTrailEntry(viewport, bucket)) {
        this.resizeOverviewTrailCanvas();
        return;
      }

      if (this.drawOverviewTrailViewport(viewport, bucket)) {
        this.latestOverviewTrailKey = this.getOverviewTrailKey(viewport, bucket);
      }
    },
    normalizeOverviewViewport(viewport) {
      let normalized = ['left', 'top', 'width', 'height'].reduce((styles, key) => {
        if (viewport[key] === undefined || viewport[key] === null) {
          return styles;
        }
        styles[key] = viewport[key];
        return styles;
      }, {});

      if (!normalized.left || !normalized.top || !normalized.width || !normalized.height) {
        return null;
      }

      if (parseFloat(normalized.width) <= 0 || parseFloat(normalized.height) <= 0) {
        return null;
      }

      return normalized;
    },
    isDuplicateOverviewTrailEntry(viewport, bucket) {
      return this.latestOverviewTrailKey === this.getOverviewTrailKey(viewport, bucket);
    },
    getOverviewTrailKey(viewport, bucket) {
      return [
        bucket.magnification,
        viewport.left,
        viewport.top,
        viewport.width,
        viewport.height,
      ].join(':');
    },
    getOverviewTrailBucket(magnification) {
      return this.overviewTrailBuckets
        .slice()
        .reverse()
        .find(bucket => magnification >= bucket.magnification) || this.overviewTrailBuckets[0];
    },
    getOverviewTrailColor(index, maxIndex) {
      let colorIndex = Math.round((index / maxIndex) * (OVERVIEW_TRAIL_HEATMAP_COLORS.length - 1));
      return OVERVIEW_TRAIL_HEATMAP_COLORS[colorIndex];
    },
    getOverviewTrailOpacity(index, maxIndex) {
      let opacity = OVERVIEW_TRAIL_MIN_OPACITY + ((index / maxIndex) * (OVERVIEW_TRAIL_MAX_OPACITY - OVERVIEW_TRAIL_MIN_OPACITY));
      return Math.round(opacity * 100) / 100;
    },
    drawOverviewTrailViewport(viewport, bucket) {
      if (!bucket) {
        return false;
      }

      this.resizeOverviewTrailCanvas();
      let layerCanvas = this.getOverviewTrailLayerCanvas(bucket);
      let context = layerCanvas?.getContext('2d');
      if (!context) {
        return false;
      }

      context.globalAlpha = 1;
      context.globalCompositeOperation = 'source-over';
      context.fillStyle = bucket.color;
      context.fillRect(
        this.toCanvasCoordinate(viewport.left, layerCanvas.width),
        this.toCanvasCoordinate(viewport.top, layerCanvas.height),
        this.toCanvasCoordinate(viewport.width, layerCanvas.width),
        this.toCanvasCoordinate(viewport.height, layerCanvas.height),
      );
      this.overviewTrailTouchedBuckets.add(bucket.magnification);
      this.hasOverviewTrailCanvasContent = true;
      return true;
    },
    composeOverviewTrailCanvas() {
      this.resizeOverviewTrailCanvas();
    },
    getOverviewTrailLayerRef(bucket) {
      return `overviewTrailLayer${String(bucket?.magnification).replace(/[^0-9a-z]/gi, '_')}`;
    },
    getOverviewTrailLayerStyle(bucket) {
      return {
        opacity: bucket.opacity,
      };
    },
    getOverviewTrailRefElement(refName) {
      let ref = this.$refs[refName];
      return Array.isArray(ref) ? ref[0] : ref;
    },
    getOverviewTrailLayerCanvas(bucket) {
      if (!bucket) {
        return null;
      }

      return this.getOverviewTrailRefElement(this.getOverviewTrailLayerRef(bucket));
    },
    resizeOverviewTrailCanvas() {
      let layerCanvases = this.overviewTrailBuckets
        .map(bucket => this.getOverviewTrailLayerCanvas(bucket))
        .filter(Boolean);
      if (!layerCanvases.length) {
        return;
      }

      let firstCanvas = layerCanvases[0];
      let bounds = firstCanvas.getBoundingClientRect();
      let width = Math.round(bounds.width || firstCanvas.clientWidth || parseFloat(this.overviewStyle.width) || firstCanvas.width || 300);
      let height = Math.round(bounds.height || firstCanvas.clientHeight || parseFloat(this.overviewStyle.height) || firstCanvas.height || 150);
      layerCanvases.forEach(layerCanvas => {
        if (layerCanvas.width !== width || layerCanvas.height !== height) {
          let snapshotCanvas = document.createElement('canvas');
          snapshotCanvas.width = width;
          snapshotCanvas.height = height;
          snapshotCanvas.getContext('2d')?.drawImage(layerCanvas, 0, 0, width, height);
          layerCanvas.width = width;
          layerCanvas.height = height;
          layerCanvas.getContext('2d')?.drawImage(snapshotCanvas, 0, 0);
        }
      });
    },
    toCanvasCoordinate(value, canvasSize) {
      if (typeof value === 'string' && value.trim().endsWith('%')) {
        return (parseFloat(value) / 100) * canvasSize;
      }
      return Number(value) || 0;
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/dark-variables';
@import '../../assets/styles/viewer-layers';

.viewer-image-overlays {
  pointer-events: none;
}

.viewer-image-overlays > * {
  pointer-events: auto;
}

.image-draw-tools {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  right: 1rem;
  z-index: $viewer-z-image-controls;
  pointer-events: none;
}

.image-draw-tools :deep(.draw-tools-wrapper) {
  pointer-events: auto;
}

.annotation-details-overlay {
  position: absolute;
  top: 5.75rem;
  right: 0.5rem;
  width: 20rem;
  height: auto;
  max-height: calc(100% - 3rem);
  overflow: visible;
  background-color: rgba($dark-bg-primary, 0.96);
  color: $dark-text-primary;
  opacity: 0.95;
  border: 1px solid rgba(222, 238, 255, 0.12);
  border-radius: 8px;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.28);
  box-sizing: border-box;
  z-index: 1000;
}

.custom-overview {
  position: absolute;
  bottom: 0.5rem;
  left: 0.5rem;
  background: rgba(8, 17, 29, 0.82);
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(222, 238, 255, 0.14);
  border-radius: 6px;
  overflow: visible;
}

.overview-image {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 0 0 4px 4px;
  cursor: crosshair;
}

.overview-image img {
  display: block;
  position: absolute;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  object-fit: fill;
  user-select: none;
}

.overview-trail-layers {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;

  &.is-hidden {
    display: none;
  }
}

.overview-trail-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.overview-viewport {
  position: absolute;
  z-index: 2;
  box-sizing: border-box;
  border: 2px solid rgba($primary, 0.9);
  background: rgba($primary, 0.12);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.65);
  pointer-events: none;
}

.overview-trail-controls {
  position: absolute;
  top: 0.35rem;
  right: 0.35rem;
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.overview-trail-toggle,
.overview-trail-reset {
  width: 1.85rem;
  height: 1.85rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid rgba(222, 238, 255, 0.28);
  border-radius: 999px;
  background: rgba(8, 17, 29, 0.74);
  color: rgba(236, 246, 255, 0.92);
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.24);

  &:hover,
  &:focus {
    background: rgba(14, 28, 44, 0.9);
    border-color: rgba($primary, 0.72);
    outline: none;
  }

  &.is-off {
    color: rgba(236, 246, 255, 0.58);
  }
}

.overview-trail-reset {
  font-size: 0.9rem;
}

.overview-scale-line {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 100%;
  box-sizing: border-box;
  border-radius: 4px 4px 0 0;
  width: 100%;
  min-width: 0;
  background: rgba(8, 17, 29, 0.92);
  border: 1px solid rgba(222, 238, 255, 0.14);
  border-bottom: 0;
}

@media (max-width: 768px) {
  .custom-overview {
    display: none;
  }

  .image-draw-tools {
    left: 2.6rem;
    right: 0.5rem;
    overflow-x: auto;
    white-space: nowrap;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}
</style>
